import React from "react";
import {
  Armchair,
  BookOpen,
  ChevronRight,
  Compass,
  HelpCircle,
  MessageCircle,
  Plane,
  ShieldCheck,
  Smartphone,
  Star,
  UsersRound,
  Wifi,
} from "lucide-react";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./Tabs";
import "./helpcenter.scss";
import {get} from "../../utils/request";

/* ============================
   Robust article fetch hook
   ============================ */
function useExternalArticle(articleId, latestTitle = "") {
  const [state, setState] = React.useState({
    loading: true,
    title: "",
    content: "",
    isHtml: false,
  });

  React.useEffect(() => {
    const controller = new AbortController();
    const wantId = String(articleId ?? "").trim();

    const hasHtml = (s) =>
      /<\s*(p|h1|h2|h3|div|ul|ol|li|br|strong|em|span)\b/i.test(s || "");

    const load = async () => {
      setState((s) => ({ ...s, loading: true }));

      try {
        const result = latestTitle
          ? await get("/article/latest", { title: latestTitle })
          : await get(`/article/id/${wantId}`);

        let picked = result;
        let title = "";
        let content = "";
        let isHtml = false;

        title = picked?.title || picked?.name || "";
        content =
          picked?.content ||
          picked?.body ||
          picked?.markdown ||
          picked?.html ||
          picked?.description ||
          "";

        // If content still empty, scan nested strings for HTML
        if (!content) {
          const findFirstHtmlString = (obj, depth = 0) => {
            if (!obj || depth > 6) return "";
            if (typeof obj === "string") return hasHtml(obj) ? obj : "";
            if (Array.isArray(obj)) {
              for (const v of obj) {
                const f = findFirstHtmlString(v, depth + 1);
                if (f) return f;
              }
              return "";
            }
            if (typeof obj === "object") {
              for (const k of Object.keys(obj)) {
                const f = findFirstHtmlString(obj[k], depth + 1);
                if (f) return f;
              }
            }
            return "";
          };
          content =
            findFirstHtmlString(picked) || "";
        }

        isHtml = hasHtml(content);

        setState({
          loading: false,
          title,
          content: content || "No content available.",
          isHtml,
        });
      } catch (e) {
        if (e.name === "AbortError") return;
        console.error("Error fetching article:", e);
        setState({
          loading: false,
          title: "",
          content: "Unable to load content. Please try again later.",
          isHtml: false,
        });
      }
    };

    load();
    return () => controller.abort();
  }, [articleId, latestTitle]);

  return state;
}

const helpArticles = [
  {
    key: "general",
    label: "General",
    title: "Account, bookings, and app basics",
    description: "Start here for account access, profile settings, rewards, and general Teleport Now questions.",
    icon: HelpCircle,
  },
  {
    key: "esim",
    label: "Travel eSIM",
    title: "eSIM setup and data support",
    description: "Find answers for compatible phones, activation, data plans, installation, and troubleshooting.",
    icon: Wifi,
  },
  {
    key: "flights",
    label: "Flights",
    title: "Flight search and booking help",
    description: "Get help with fare search, traveler details, checkout, ticketing, and booking updates.",
    icon: Plane,
  },
  {
    key: "thingsToDo",
    label: "Things To Do",
    title: "Activities and experience support",
    description: "Learn about availability, visit details, traveller requirements, and activity bookings.",
    icon: Compass,
  },
  {
    key: "tellyMembership",
    label: "Telly Membership",
    title: "Membership, tiers, and rewards",
    description: "Understand Telly tiers, credits, rewards, referrals, and membership benefits.",
    icon: Star,
  },
  {
    key: "lounge",
    label: "Airport Lounge",
    title: "Airport lounge access support",
    description: "Get help choosing lounges, confirming access, traveller details, and visit requirements.",
    icon: Armchair,
  },
  {
    key: "moments",
    label: "Moments",
    title: "Teleport Moments help",
    description: "Find answers about posts, profiles, followers, community interactions, and account safety.",
    icon: UsersRound,
  },
];

const supportCards = [
  {
    icon: Smartphone,
    title: "App-first support",
    body: "Manage eSIMs, bookings, and profile details from your Teleport Now account.",
  },
  {
    icon: ShieldCheck,
    title: "Secure checkout",
    body: "Payment and order flows stay connected to the existing Teleport Now backend.",
  },
  {
    icon: MessageCircle,
    title: "Need a human?",
    body: "Use the contact page when your question needs account-specific support.",
    href: "/contact-us",
  },
];

function ArticleContent({ article, state }) {
  const Icon = article.icon;

  return (
    <article className="faq-content">
      <div className="faq-content__heading">
        <div className="faq-content__icon">
          <Icon size={22} />
        </div>
        <div>
          <p>{article.label} FAQ</p>
          <h2>{state.title || article.title}</h2>
          <span>{article.description}</span>
        </div>
      </div>

      {state.loading ? (
        <div className="help-skeleton" aria-live="polite">
          <span />
          <span />
          <span />
        </div>
      ) : state.isHtml ? (
        <div className="faq-content__body" dangerouslySetInnerHTML={{ __html: state.content }} />
      ) : (
        <div className="faq-content__body">
          <p>{state.content}</p>
        </div>
      )}
    </article>
  );
}

/* ============================
   Main Help Center Page
   ============================ */
export default function HelpCenter() {
  const [active, setActive] = React.useState("general");

  // Each tab fetches a different article ID
  const general = useExternalArticle("68e1fdc4e260c9036242abf2");
  const esim = useExternalArticle("68b1973741ba6fdd830f1b66");
  const flights = useExternalArticle("68de7e3fe260c9036242abc3");
  const thingsToDo = useExternalArticle("6a1db3c909bca06bde7037fe");
  const tellyMembership = useExternalArticle("6a1db3e009bca06bde703802");
  const lounge = useExternalArticle("6a1db3f309bca06bde703806");
  const moments = useExternalArticle("", "Moments FAQ (Official)");
  const articleStates = { general, esim, flights, thingsToDo, tellyMembership, lounge, moments };
  const activeArticle = helpArticles.find((article) => article.key === active) || helpArticles[0];
  const ActiveIcon = activeArticle.icon;

  return (
    <>
      <Navbar />

      <div className="help-center-page">
        <div className="container">
          <section className="help-hero">
            <div className="help-hero__copy">
              <p className="help-hero__eyebrow">Teleport Now Support</p>
              <h1 className="help-center-title">How can we help?</h1>
              <p className="help-center-subtitle">
                Get fast answers for travel eSIMs, flights, bookings, checkout, and account support.
              </p>
            </div>

            <div className="help-hero__panel" aria-label="Current support category">
              <div className="help-hero__panel-icon">
                <ActiveIcon size={26} />
              </div>
              <p>Now viewing</p>
              <h2>{activeArticle.label} FAQ</h2>
              <span>{activeArticle.description}</span>
            </div>
          </section>

          <section className="help-support-grid" aria-label="Support options">
            {supportCards.map((card) => {
              const Icon = card.icon;
              const content = (
                <>
                  <Icon size={22} />
                  <div>
                    <h3>{card.title}</h3>
                    <p>{card.body}</p>
                  </div>
                  {card.href && <ChevronRight size={18} />}
                </>
              );

              return card.href ? (
                <a key={card.title} href={card.href} className="help-support-card">
                  {content}
                </a>
              ) : (
                <div key={card.title} className="help-support-card">
                  {content}
                </div>
              );
            })}
          </section>

          <div className="help-center-layout">
            <aside className="help-center-sidebar">
              <div className="help-sidebar-card">
                <BookOpen size={22} />
                <h2>Support library</h2>
                <p>Choose a topic and read the latest help article loaded from the backend.</p>
              </div>
            </aside>

            <div className="help-center-tabs">
              <Tabs value={active} onValueChange={setActive}>
                <TabsList>
                  {helpArticles.map((article) => {
                    const Icon = article.icon;
                    return (
                      <TabsTrigger key={article.key} value={article.key}>
                        <Icon size={18} />
                        <span>{article.label}</span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {helpArticles.map((article) => (
                  <TabsContent key={article.key} value={article.key}>
                    <ArticleContent article={article} state={articleStates[article.key]} />
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}
